/**
 * localStorage 键名迁移配置
 */
const KEY_MIGRATION_RULES = {
  // 固定键名映射
  exact: {
    "home-memo-editor": "home-journal-editor",
    "memos-locale": "journals-locale",
    "memos-theme": "journals-theme",
    "memos-view-setting": "journals-view-setting",
  } as Record<string, string>,

  // 前缀匹配映射（处理动态键名）
  prefix: {
    "home-memo-editor": "home-journal-editor",
    "-home-memo-editor": "-home-journal-editor",
  } as Record<string, string>,
};

/**
 * 迁移 localStorage 键名
 */
export function migrateStorageKeys(): { migrated: number; errors: string[] } {
  const result: { migrated: number; errors: string[] } = { migrated: 0, errors: [] };

  try {
    // 1. 迁移固定键名
    Object.entries(KEY_MIGRATION_RULES.exact).forEach(([oldKey, newKey]) => {
      migrateSingleKey(oldKey, newKey, result);
    });

    // 2. 迁移动态键名（前缀匹配）
    Object.entries(KEY_MIGRATION_RULES.prefix).forEach(([oldPrefix, newPrefix]) => {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;

        if (key.includes(oldPrefix)) {
          const newKey = key.replace(oldPrefix, newPrefix);
          migrateSingleKey(key, newKey, result);
        }
      }
    });

    if (result.migrated > 0) {
      // Silent migration - no console output to avoid leaking "memos" branding
    }
  } catch (error) {
    result.errors.push(`Migration failed: ${error instanceof Error ? error.message : String(error)}`);
    // Silent error handling - no console output
  }

  return result;
}

/**
 * 迁移单个键名
 */
function migrateSingleKey(oldKey: string, newKey: string, result: { migrated: number; errors: string[] }): void {
  if (localStorage.getItem(newKey) !== null) {
    // Silent skip - no console output
    return;
  }

  const oldValue = localStorage.getItem(oldKey);
  if (oldValue === null) return;

  try {
    localStorage.setItem(newKey, oldValue);
    localStorage.removeItem(oldKey);
    result.migrated++;
    // Silent migration - no console output
  } catch (error) {
    result.errors.push(`Failed to migrate ${oldKey}: ${error}`);
    // Silent error handling - no console output
  }
}

/**
 * 降级读取：优先读新键，不存在则读旧键
 */
export function getStorageItem(key: string): string | null {
  const newValue = localStorage.getItem(key);
  if (newValue !== null) return newValue;

  const { exact } = KEY_MIGRATION_RULES;
  const oldKeyEntries = Object.entries(exact).filter(([, newKey]) => newKey === key);

  for (const [oldKey] of oldKeyEntries) {
    const oldValue = localStorage.getItem(oldKey);
    if (oldValue !== null) {
      // Silent fallback - no console output to avoid leaking "memos" branding
      return oldValue;
    }
  }

  return null;
}
